/**
 * Usage analytics: automatic page-view + click tracking.
 *
 * Events queue in memory and flush in batches to POST /audit/usage
 * (lambda/mysql-audit.mjs -> dbo.tblUsageEvents) every FLUSH_INTERVAL_MS,
 * plus a best-effort flush when the tab is hidden/closed. Flushes go through
 * apiClient (with {__skipAudit: true}) because the route sits behind the
 * Cognito authorizer — navigator.sendBeacon can't carry the Authorization
 * header, so losing the final partial batch on close is accepted.
 *
 * Never records anything on the public customer portal or while logged out,
 * and never captures input values — only button/link labels and paths.
 */

import { apiClient } from "./apiClient";
import { brand } from "../config/brand";

const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 200;
const MAX_BATCH = 100;
const PUBLIC_PATH_PREFIX = "/customer/inventory/";
const SESSION_KEY = `${brand.id}-analytics-session`;

let queue = [];
let flushTimer = null;
let initialized = false;
let flushing = false;

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function isPublicPath(path) {
  return (path || "").startsWith(PUBLIC_PATH_PREFIX);
}

/** Queue one event. No-op on the public portal. */
export function track(type, payload = {}) {
  const path = payload.path || window.location.pathname;
  if (isPublicPath(path)) return;

  queue.push({
    type,
    path,
    label: payload.label || null,
    elementId: payload.elementId || null,
    metadata: payload.metadata || null,
    ts: Date.now(),
  });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
}

export function trackPageView(path) {
  track("page_view", { path });
}

/** Send queued events. Failed batches are re-queued once (drop on repeat). */
export async function flush() {
  if (flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(batch.length);
  try {
    await apiClient.post(
      "/audit/usage",
      { sessionId: getSessionId(), events: batch },
      { __skipAudit: true },
    );
  } catch {
    if (!batch[0]?._retried) {
      batch.forEach((evt) => {
        evt._retried = true;
      });
      queue = batch.concat(queue).slice(0, MAX_QUEUE);
    }
  } finally {
    flushing = false;
  }
}

function handleDocumentClick(event) {
  try {
    const el = event.target?.closest?.('button, a, [role="button"]');
    if (!el) return;

    const label = (el.getAttribute("aria-label") || el.textContent || "")
      .trim()
      .replace(/\s+/g, " ")
      .slice(0, 120);
    const elementId = el.id || null;
    // Icon-only buttons with no accessible name aren't worth a row
    if (!label && !elementId) return;

    const context =
      el
        .closest("[data-analytics-context]")
        ?.getAttribute("data-analytics-context") ||
      document.title ||
      null;

    track("click", {
      label: label || null,
      elementId,
      metadata: {
        tag: el.tagName.toLowerCase(),
        href: el.tagName === "A" ? el.getAttribute("href") : undefined,
        context,
      },
    });
  } catch {
    // Analytics must never break the click it observed.
  }
}

function handleVisibilityChange() {
  if (document.visibilityState === "hidden") flush();
}

/**
 * Install the global click listener and flush loop. Call once after login;
 * safe to call repeatedly. Returns a teardown function (used by tests).
 */
export function initAnalytics() {
  if (initialized) return () => {};
  initialized = true;

  document.addEventListener("click", handleDocumentClick, true);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", flush);
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  return function teardown() {
    initialized = false;
    document.removeEventListener("click", handleDocumentClick, true);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", flush);
    if (flushTimer) clearInterval(flushTimer);
    flushTimer = null;
    queue = [];
  };
}
